/*
 * Repository picker. The list is whatever GitHub returned for this user, so it
 * is already filtered to what they may see, so there is no client-side access
 * check here because there is nothing to hide.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Archive, CaretDown, Lock, Search } from './Icons'
import { rel } from '../lib/format'
import type { RepoSummary } from '../types'

export function RepoPicker({
  repos, value, onSelect, loading
}: {
  repos: RepoSummary[]
  value: string | null
  onSelect: (name: string) => void
  loading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return repos
    return repos.filter(r => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
  }, [repos, query])

  useEffect(() => { setActive(0) }, [query, open])

  useEffect(() => {
    if (!open) return
    input.current?.focus()
    const onDocDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [open])

  const choose = (repo: RepoSummary) => {
    setOpen(false)
    setQuery('')
    onSelect(repo.name)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, matches.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && matches[active]) { e.preventDefault(); choose(matches[active]) }
  }

  return (
    <div className="picker" ref={root}>
      <button
        type="button"
        className="picker-trigger"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading && repos.length === 0}
      >
        <span className="label">{value ?? (loading ? 'Loading repositories…' : 'Select a repository')}</span>
        <span className="caret"><CaretDown size={14} /></span>
      </button>

      {open ? (
        <div className="picker-pop" role="listbox">
          <div className="picker-search">
            <Search size={15} />
            <input
              ref={input}
              value={query}
              placeholder={`Search ${repos.length} repositories…`}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Search repositories"
            />
          </div>
          <div className="picker-list">
            {matches.length === 0 ? (
              <div className="picker-empty">No repository matches “{query}”.</div>
            ) : (
              matches.map((repo, i) => (
                <button
                  type="button"
                  key={repo.fullName}
                  className={`picker-item ${i === active ? 'active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(repo)}
                  role="option"
                  aria-selected={repo.name === value}
                >
                  <span style={{ color: 'var(--color-neutral-500)', display: 'inline-flex' }}>
                    {repo.archived ? <Archive size={14} /> : repo.private ? <Lock size={13} /> : null}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span className="nm" style={{ display: 'block' }}>{repo.name}</span>
                    {repo.description ? <span className="ds" style={{ display: 'block' }}>{repo.description}</span> : null}
                  </span>
                  <span className="rt">{rel(repo.pushedAt)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
